import { LegendsDot } from '../Legends/Legends.styles';
import {
  Arrow,
  ChartToolTipStyled,
  HeaderTextContainer,
  ToolTipArea,
  ToolTipBody,
  ToolTipContainer,
  ToolTipDotContainer,
  ToolTipHeader,
  TooltipTextContainer,
  TooltipValueContainer,
} from './ChartToolTip.styles';
import { ComponentProps, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

type Props = ComponentProps<typeof ChartToolTipStyled> & {
  root?: Parameters<typeof usePopper>[0];
  onClick?: () => void;
  chartData: {
    title: string;
    titleCounter?: number | string;
    titleDotColor?: string | undefined;
    data: {
      dotColor?: string | null;
      counter?: number | string;
      text: string;
    }[];
  };
  options?: Parameters<typeof usePopper>[2];
  blurScroll?: boolean;
};

export const ChartToolTip = ({
  root,
  onClick,
  chartData,
  onMouseEnter,
  onMouseLeave,
  blurScroll,
  ...props
}: Props) => {
  const tooltipElement = useRef<HTMLDivElement>(null);
  const [arrow, setArrow] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(root, tooltipElement.current, {
    modifiers: [
      { name: 'arrow', options: { element: arrow } },
      {
        name: 'eventListeners',
        options: { scroll: false },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 7.5],
        },
      },
    ],
    placement: 'bottom',
    ...props.options,
  });
  return (
    <ChartToolTipStyled
      ref={tooltipElement}
      style={styles.popper}
      {...attributes.popper}
      data-testid={'tooltip-frame'}
      {...props}
    >
      <ToolTipContainer>
        <ToolTipHeader title={chartData.title} blurScroll={blurScroll}>
          <ToolTipDotContainer>
            {chartData.titleDotColor && (
              <LegendsDot
                color={chartData.titleDotColor}
                data-testid={'legends-dot'}
              />
            )}
            <HeaderTextContainer>{chartData.title}</HeaderTextContainer>
          </ToolTipDotContainer>
          <TooltipValueContainer>
            {chartData.titleCounter ?? ''}
          </TooltipValueContainer>
        </ToolTipHeader>
        <div>
          <ToolTipArea
            blurScroll={blurScroll}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {chartData.data.map((item) => (
              <ToolTipBody key={item.text}>
                <ToolTipDotContainer>
                  {item.dotColor && (
                    <LegendsDot
                      color={item?.dotColor}
                      data-testid={'legends-dot'}
                    />
                  )}
                  <TooltipTextContainer title={item?.text}>
                    {item.text ?? ''}
                  </TooltipTextContainer>
                </ToolTipDotContainer>
                <TooltipValueContainer>
                  {item.counter ?? ''}
                </TooltipValueContainer>
              </ToolTipBody>
            ))}
          </ToolTipArea>
        </div>
      </ToolTipContainer>
      <Arrow ref={setArrow} style={styles.arrow} data-testid={`down-arrow`} />
    </ChartToolTipStyled>
  );
};
